/** @jsxImportSource @emotion/react */
import apiUrl from "api-url";
import axios from "axios";
import { AsyncPaginate } from "react-select-async-paginate";
import { components, OptionProps } from "react-select";

import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";

const LocationOption = (props: OptionProps<any, false>) => {
  const { isFocused, innerRef, innerProps, children } = props;

  // styles
  let bg = "transparent";
  if (isFocused) {
    bg = "#eee";
  }

  const style = {
    alignItems: "center",
    backgroundColor: bg,
    color: "inherit",
    display: "flex ",
  };

  return (
    <div ref={innerRef} {...innerProps} style={style}>
      <components.Option {...props}>{children}</components.Option>
    </div>
  );
};

type SearchByLocationParams = {
  fvId: string;
  redirectToInsightsView: (locationId: number) => void;
};

type SelectedLocation = {
  label: string;
  value: number;
  data: any;
};

type LoadOptionsReturn = {
  options: SelectedLocation[];
  hasMore: boolean;
  additional: {
    pageNumber: number;
    prevValue: string;
  };
};

type LocationData = {
  id: number;
  name: string;
  code: string;
  [key: string]: any;
};

type MetaData = {
  currentPage: number;
  totalPages: number;
};

type ApiResponse = {
  data: LocationData[];
  meta: MetaData;
};

export const SearchByLocation = ({
  fvId,
  redirectToInsightsView,
}: SearchByLocationParams) => {
  const { t } = useTranslation("inv-dashboard");

  const [selectedLocation, setSelectedLocation] =
    useState<SelectedLocation | null>(null);

  const [searchText, setSearchText] = useState("");
  const [isEdited, setIsEdited] = useState(false);

  useEffect(() => {
    if (selectedLocation) {
      const locationId = selectedLocation.value && selectedLocation.value;
      redirectToInsightsView(locationId);
    }
  }, [selectedLocation, redirectToInsightsView]);

  const loadLocations = async (
    currentVal: string,
    pageNumber: number,
  ): Promise<LoadOptionsReturn> => {
    try {
      const url = apiUrl(
        `/entity-inventory/location-visibility/organization/${fvId}?pageSize=5&pageNumber=${pageNumber}&global_search_term=${searchText}`,
      );

      const res = await axios.get<ApiResponse>(url);

      const data = res.data.data.map((val) => ({
        label: `${val.name} - ${val.code}`,
        value: val.id,
        data: val,
      }));

      const metaData = res.data.meta;

      return {
        options: data,
        hasMore: metaData.currentPage + 1 < metaData.totalPages,
        additional: {
          pageNumber,
          prevValue: currentVal,
        },
      };
    } catch (err) {
      console.error(err);
      return {
        options: [],
        hasMore: false,
        additional: {
          pageNumber,
          prevValue: currentVal,
        },
      };
    }
  };

  return (
    <div
      css={{
        marginLeft: "auto",
        minWidth: "400px",
      }}
    >
      <AsyncPaginate
        debounceTimeout={500}
        components={{
          Option: LocationOption,
          DropdownIndicator: () => null,
        }}
        onInputChange={(val) => {
          setSearchText(val);
        }}
        hideSelectedOptions={false}
        closeMenuOnSelect={true}
        value={selectedLocation}
        loadOptions={async (
          currentVal,
          options,
          additionalOptions:
            | { pageNumber: number; prevValue: string }
            | undefined,
        ) => {
          if (searchText.length > 1) {
            const data = await loadLocations(
              currentVal,
              additionalOptions && currentVal === additionalOptions?.prevValue
                ? additionalOptions.pageNumber + 1
                : 0,
            );
            return data;
          }

          return {
            options: [],
            hasMore: false,
            additional: { pageNumber: 0, prevValue: currentVal },
          };
        }}
        cacheUniqs={[searchText]}
        onChange={(selectedData) => {
          if (!isEdited) {
            setIsEdited(true);
          }
          setSelectedLocation(selectedData ?? null);
        }}
        placeholder={t("inv-dashboard:Search for Location")}
        noOptionsMessage={() => null}
      />
    </div>
  );
};
